/**
 * Pure rollup builders — the study/coordinator aggregation that used to live
 * inside useInsightsData's useMemo bodies. Extracted (Fix Spec C) so
 * InsightsContext can cache the result across page remounts (tab switches)
 * instead of recomputing from a per-component useMemo that resets on every
 * mount, and so the aggregation logic is independently unit-testable without
 * rendering anything.
 */

import { EffortRow, VisitStatsRow, StudyInfo, lensTotal } from '../types';

// Identity keys: a coordinator is a user when mapped, else the raw CRC
// string; '' collapses to the explicit unattributed bucket.
export const UNATTRIBUTED_KEY = 'unattributed';
export const coordinatorKey = (userId: string | null, crcName: string) =>
  userId ? `user:${userId}` : crcName ? `raw:${crcName}` : UNATTRIBUTED_KEY;
export const studyKey = (protocolId: string | null, studyName: string) =>
  protocolId ? `study:${protocolId}` : studyName ? `rawstudy:${studyName}` : 'rawstudy:(none)';

export interface StudyRollup {
  key: string;
  protocol_id: string | null;
  raw_name: string; // display for unmapped buckets
  minutesByStatus: Record<string, number>;
  visit_count: number;
  completed_count: number;
  in_flight_count: number;
  incomplete_count: number;
  override_minutes: number;
  override_count: number;
  /** coordinatorKey -> minutes (all statuses; lens applied at render) */
  coordinatorStatusMinutes: Map<string, Record<string, number>>;
}

export interface CoordinatorRollup {
  key: string;
  user_id: string | null;
  raw_names: Set<string>; // CRC strings feeding this bucket
  minutesByStatus: Record<string, number>;
  /** studyKey -> minutes-by-status */
  studyStatusMinutes: Map<string, Record<string, number>>;
  visit_count: number;
  completed_count: number;
}

const addStatusMinutes = (target: Record<string, number>, status: string, minutes: number) => {
  target[status] = (target[status] || 0) + Number(minutes || 0);
};

/**
 * Whole-visit minutes: every status key present, known or not — the same
 * total `StatusBreakdownBar` renders. Deliberately LENS-INDEPENDENT.
 *
 * This is the total behind every "did anything happen here at all?" question
 * (StudyTile's enrolling/start-up idle flags, CoordinatorCard's "could spend
 * time here" gap list). Those claims are about VISIT TIME, not about the
 * currently-selected lens: under the default `with_crc` lens a visit that
 * only ever logged `lobby` + `done` minutes sums to zero, and asking the
 * lens total would flag a study/coordinator as idle directly underneath its
 * own non-zero visit counter. Counts (`visit_count`) are deliberately NOT
 * used for this either — minutes and counts come from two different RPCs, and
 * the flags are claims about *time*.
 */
export function wholeVisitMinutes(minutesByStatus: Record<string, number>): number {
  return lensTotal(minutesByStatus, 'whole_visit');
}

/**
 * The study keys a coordinator demonstrably worked: any study with whole-visit
 * minutes > 0, in any status. Lens-independent for the reason above — the gap
 * list ("authorized minus studies with logged time") must not accuse someone
 * of ignoring a study they logged lobby/investigator time on just because the
 * page happens to be on the `with_crc` lens. The per-study rows a card DISPLAYS
 * stay lens-scoped; only membership in this set is lens-free.
 */
export function workedStudyKeys(rollup: Pick<CoordinatorRollup, 'studyStatusMinutes'> | null): Set<string> {
  const keys = new Set<string>();
  if (!rollup) return keys;
  for (const [studyKeyValue, statusMinutes] of rollup.studyStatusMinutes.entries()) {
    if (wholeVisitMinutes(statusMinutes) > 0) keys.add(studyKeyValue);
  }
  return keys;
}

/**
 * Every registered study exists even with zero activity (the tiles view must
 * show idle studies — that's the point), so `studies` seeds the map before
 * `effort`/`visits` are folded in.
 */
export function buildStudyRollups(
  studies: StudyInfo[],
  effort: EffortRow[],
  visits: VisitStatsRow[]
): Map<string, StudyRollup> {
  const map = new Map<string, StudyRollup>();
  const ensure = (protocolId: string | null, studyName: string): StudyRollup => {
    const key = studyKey(protocolId, studyName);
    let r = map.get(key);
    if (!r) {
      r = {
        key,
        protocol_id: protocolId,
        raw_name: studyName,
        minutesByStatus: {},
        visit_count: 0,
        completed_count: 0,
        in_flight_count: 0,
        incomplete_count: 0,
        override_minutes: 0,
        override_count: 0,
        coordinatorStatusMinutes: new Map(),
      };
      map.set(key, r);
    }
    return r;
  };
  for (const s of studies) ensure(s.id, s.protocol_number);
  for (const row of effort) {
    const r = ensure(row.protocol_id, row.study_name);
    addStatusMinutes(r.minutesByStatus, row.status, row.total_minutes);
    const ck = coordinatorKey(row.user_id, row.crc_name);
    const perCoord = r.coordinatorStatusMinutes.get(ck) || {};
    addStatusMinutes(perCoord, row.status, row.total_minutes);
    r.coordinatorStatusMinutes.set(ck, perCoord);
  }
  for (const row of visits) {
    const r = ensure(row.protocol_id, row.study_name);
    r.visit_count += Number(row.visit_count || 0);
    r.completed_count += Number(row.completed_count || 0);
    r.in_flight_count += Number(row.in_flight_count || 0);
    r.incomplete_count += Number(row.incomplete_count || 0);
    r.override_minutes += Number(row.override_minutes || 0);
    r.override_count += Number(row.override_count || 0);
  }
  return map;
}

export function buildCoordinatorRollups(
  effort: EffortRow[],
  visits: VisitStatsRow[]
): Map<string, CoordinatorRollup> {
  const map = new Map<string, CoordinatorRollup>();
  const ensure = (userId: string | null, crcName: string): CoordinatorRollup => {
    const key = coordinatorKey(userId, crcName);
    let r = map.get(key);
    if (!r) {
      r = {
        key,
        user_id: userId,
        raw_names: new Set(),
        minutesByStatus: {},
        studyStatusMinutes: new Map(),
        visit_count: 0,
        completed_count: 0,
      };
      map.set(key, r);
    }
    if (crcName) r.raw_names.add(crcName);
    return r;
  };
  for (const row of effort) {
    const r = ensure(row.user_id, row.crc_name);
    addStatusMinutes(r.minutesByStatus, row.status, row.total_minutes);
    const sk = studyKey(row.protocol_id, row.study_name);
    const perStudy = r.studyStatusMinutes.get(sk) || {};
    addStatusMinutes(perStudy, row.status, row.total_minutes);
    r.studyStatusMinutes.set(sk, perStudy);
  }
  for (const row of visits) {
    const r = ensure(row.user_id, row.crc_name);
    r.visit_count += Number(row.visit_count || 0);
    r.completed_count += Number(row.completed_count || 0);
  }
  return map;
}
